/**
 * 登录 sdk 配置描述文件
 */
import { AuthSource, PlatformSource } from '@byted-star/enums';
import Cookies from 'js-cookie';
import { find, merge, get } from 'lodash-es';
import { stringify } from 'qs';
import type {
  InterfaceNormalList,
  InterfacePlatformSource,
  InterfaceSdkForm,
  InterfaceTab,
  InterfaceUser,
  InterfaceUserPanel,
  TypeObjectString,
} from './Interface';
import defaultConfig, {
  loginTabs,
  OauthConfigLink,
  OauthList,
  switchTypeList,
  UserRole,
} from './config';
import type { SwitchType } from './enums';
import { PanelType } from './enums';


/**
 * getOauthTypes() 返回当前应该展示的第三方登录
 */
export const getOauthTypes = (
  switchType: `${SwitchType}`,
  panel: `${PanelType}`,
  role: Number
) => {
  const defaultOauthList = OauthList.map((item) => {
    return item.oauthKey ?? item.key;
  });
  // 获取到当前角色配置
  const user = find(UserRole, { value: role }) as InterfaceUser;
  const oauthList = get(
    user,
    `${switchType}.${panel}.oauthList`,
    defaultOauthList
  );
  return oauthList;
};

/**
 *  getPanel() 返回当前界面需要展示的 panel
 */
export const getPanel = (
  switchType: `${SwitchType}`,
  panel: `${PanelType}`,
  role: number
): InterfaceUserPanel => {
  // 获取到当前角色配置
  const user = find(UserRole, { value: role }) as InterfaceUser;
  return get(user, `${switchType}.${panel}.panel`) ?? {};
};

/**
 * getTabs() 返回当前界面渲染的tabList和应该要选中的tab
 * @param {number} role  用户角色
 * @param {string} switchType   当前的类型
 * @param {string} panel   当前的状态
 * @return {object} {choose, list}
 *
 */
export const getTabs = (
  role: Number,
  switchType: `${SwitchType}`,
  panel: `${PanelType}`
): {
  choose: InterfaceTab;
  list: InterfaceTab[];
} => {
  // 获取到当前角色配置
  const user = find(UserRole, { value: role });
  // 获取当前全部的 tab
  const switchArr: InterfaceNormalList[] = [];
  switchTypeList.forEach((item) => {
    if (get(user, `${item.value}.${panel}.show`)) {
      switchArr.push(item);
    }
  });

  // 获取当前聚焦类型的 tab
  const choose = find(loginTabs, {
    include: [switchType, panel],
  }) as InterfaceTab;

  return {
    list: switchArr.map((item) => {
      return find(loginTabs, { include: [item.value, panel] }) as InterfaceTab;
    }),
    choose,
  };
};

/**
 * 获取当前角色，登录类型下回调 url
 */
export const getCallBackUrl = (
  isBoe: boolean,
  panel: `${PanelType}`,
  role: number,
  site: string,
  media: string,
): string => {
  // 获取到当前角色配置
  const user = find(UserRole, { value: role }) as InterfaceUser;
  const oauth = isBoe ? OauthConfigLink.boe : OauthConfigLink.production;
  let from = get(oauth, `${panel}.callback.${user.role}`, '');
  let extendParams = {
    role: user.role,
    sso: 1,
    login: true,
    site,
    login_source: AuthSource.pc,
    platform_source: -1,
    oauth_callback: '',
    login_type: '',
  };
  // 添加媒体来源 
  const keys = PlatformSource.metas();
  keys.forEach((item) => {
    if (item.key === media || item.oauthKey === media) {
      extendParams.platform_source = item.value;
    }
  })
  // 这里针对 mcn 添加上参数，不然无法验证属性 0918第一期默认抖音 mcn
  if (user.role === 'mcn') {
    // 第一期暂时兼容头条和抖音跳转
    extendParams = {
      ...extendParams,
      oauth_callback: 'mcn',
    }
    // 替换固定 url
    if (extendParams.platform_source === -1) {
      from = from.replace('/v/api/mcn/oceanengine_callback', '/mcn');
    }
    Cookies.set('star_login_type', 'mcn_oceanengine');
  }
  if (user.role === 'ad') {
    extendParams = {
      ...extendParams,
      oauth_callback: 'demander',
    }
  }
  if (user.role === 'sup') {
    extendParams = {
      ...extendParams,
      oauth_callback: 'author',
      login_type: 'sup_douyin',
    }
  }
  from = `${from}?${stringify({
    ...extendParams,
  })}`;
  return from;
};

/** getOauth 获取第三方跳转的配置
 * @mcn 跳转，目前只支持登录
 * @login_source 入口位置 1 pc
 * @platform_source 类型- 1抖音
 *
 */
export const getOauth = (
  isBoe: boolean,
  panel: `${PanelType}`,
  role: number,
  site: string
) => {
  if (panel === PanelType.forgetPwd) {
    return [];
  }
  // 获取到当前角色配置
  const user = find(UserRole, { value: role }) as InterfaceUser;
  const oauth = isBoe ? OauthConfigLink.boe : OauthConfigLink.production;
  const path = oauth[panel];
  const obj: TypeObjectString = {};
  OauthList.forEach((item: InterfacePlatformSource) => {
    // 这里需要动态的取到当前需要回调的地址，也就是登录成功后跳转的地址
    const key = item.oauthKey ?? item.key;
    const from = getCallBackUrl(isBoe, panel, role, site, key);
    obj[key] = {
      link: `${oauth.protocol}://${oauth.host}${path.pathname}?${stringify({
        ...path.query,
        role: user.role,
        oauth: key,
        from,
      })}`,
    };
  });
  return obj;
};

/**
 * 根据当前情况生成对应配置文件
 * 1. 当前角色 user  --- 广告主 | 达人 |  mcn
 * 2. 当前状态 switch_type  --- login | register | | 'bind' | forgetPwd | verifylogin
 * 3. 当前类型 panel ---  email | mobile | code
 * 4. 第三方类型跳转 other ---  'toutiao' | 'douyin' | 'ixigua' | 'huoshan'
 */
export const getConfig = (userConfig: any, role: number) => {
  // 如果 role 不在角色，默认客户
  if (UserRole.findIndex((item) => item.value === role) === -1) {
    role = 1;
  }
  const current = merge({}, defaultConfig, userConfig);
  const defaultSwitch = current.switchConfig.defaultSwitch;
  const defaultPanel = current.switchConfig.defaultPanel;
  // 处理当前状态下，支持切换的类型，注册 -> 忘记密码 -> 登录 panel 显示
  const panel = getPanel(defaultSwitch, defaultPanel, role);
  // 处理第三方跳转链接， 0916,登录一期通过 oc 跳板进行跳转三方登录
  const oauth = getOauth(current.isBoe, defaultPanel, role, current.site);
  // 获取当前需要展示的第三方链接
  const showOauthTypes = getOauthTypes(defaultSwitch, defaultPanel, role);
  // 处理回调地址
  const ssoConfigs: TypeObjectString = {};
  ssoConfigs[defaultPanel] = {
    callback_url: getCallBackUrl(
      current.isBoe,
      defaultPanel,
      role,
      current.site,
      ''
    ),
  };
  return merge({}, current, {
    switchConfig: {
      panel,
    },
    oauthConfig: {
      ...oauth,
    },
    showOauthTypes,
    ssoConfigs,
  });
};

/**
 * 根据表单的情况，返回当前提交按钮是否可以点击
 */
 export const getSubmitStatus = (
    form: InterfaceSdkForm, 
    switchType: `${SwitchType}`,
    panel: `${PanelType}`
  ): boolean => {
  let status = true;
  const choose = find(loginTabs, {
    include: [switchType, panel],
  }) as InterfaceTab;
  // 表示当前表单存在这个字段, 获取到当前状态下表单需要校验的字段
  const valid = choose.formValid ?? [];
  // console.log(valid, form);
  valid.forEach(item => {
    const key = item as keyof InterfaceSdkForm;
    const cur = form[key];
    // 有一个不通过就为 false
    if (cur.vpattern) {
      const reg = new RegExp(cur.vpattern);
      if (!reg.test(String(cur.value))) {
        status = false;
      }
    } else if (cur.pattern) {
      const reg = new RegExp(cur.pattern);
      if (!reg.test(String(cur.value))) {
        status = false;
      }
    }
  })
  return status;
 }

 /**
  * getMessageContent
  * 根据默认的校验配置，修改校验语句
  */
 export const getMessageContent = (msg: string): string => {
   const error人Map:TypeObjectString = {
     '绑定失败，该邮箱已被绑定到另外一个帐号': '该邮箱已经注册过，请直接登录',
     '用户不存在': '该邮箱还未注册，请先完成注册',
   }
   return error人Map[msg] ?? msg;
 }